import { offersService } from '../services/offers.service';
import { networksService } from '../services/networks.service';
import { trafficSourcesService } from '../services/traffic-sources.service';
import { flowsService } from '../services/flows.service';
import { landingPagesService } from '../services/landing-pages.service';
import { postbacksService } from '../services/postbacks.service';
import { adAccountsService } from '../services/ad-accounts.service';

export interface EntityOption { id: string; name: string }

type Fetcher = (f: { search?: string; limit?: number; workspaceIds?: string[] }) => Promise<{ data: EntityOption[] }>;

/** A kind is its own id: the report filters and groups by the name. The select leaves searching to the fetcher. */
const kindFetcher = (getKinds: () => Promise<string[]>): Fetcher => async ({ search }) => ({
  data: (await getKinds())
    .filter((k) => !search || k.toLowerCase().includes(search.toLowerCase()))
    .map((k) => ({ id: k, name: k })),
});

// Map a tracking dimension field → its entity search service.
export const ENTITY_FETCHERS: Record<string, Fetcher> = {
  offer_id: (f) => offersService.query(f),
  network_id: (f) => networksService.query(f),
  network_kind: kindFetcher(networksService.getKinds),
  traffic_source_id: (f) => trafficSourcesService.query(f),
  traffic_source_kind: kindFetcher(trafficSourcesService.getKinds),
  flow_id: (f) => flowsService.query(f),
  rule_group: ({ workspaceIds, ...f }) => flowsService.listRuleGroups(f, workspaceIds),
  landing_page_id: (f) => landingPagesService.query(f),
  postback_id: (f) => postbacksService.query(f),
  ad_account_id: async (f) => {
    const r = await adAccountsService.query(f);
    return { data: r.data.map(a => ({ id: a.id, name: a.fb_account_id ? `${a.name} (${a.fb_account_id})` : a.name })) };
  },
};

/** Reports filter by these. Not Object.keys(ENTITY_FETCHERS) — that map also holds form-only pickers. */
export const ENTITY_FILTERS: Record<string, string> = {
  traffic_source_id: 'Traffic Source',
  flow_id: 'Flow',
  offer_id: 'Offer',
  landing_page_id: 'Landing Page',
  network_id: 'Network',
  traffic_source_kind: 'Traffic Source Kind',
  network_kind: 'Network Kind',
};
export const ENTITY_FILTER_ORDER = Object.keys(ENTITY_FILTERS);

export function isEntityField(field: string): boolean {
  return field in ENTITY_FILTERS;
}

/** Resolves one entity by id, for a selection outside the searched page. */
export const ENTITY_GET_BY_ID: Record<string, (id: string) => Promise<EntityOption>> = {
  offer_id: (id) => offersService.getById(id),
  network_id: (id) => networksService.getById(id),
  network_kind: async (kind) => ({ id: kind, name: kind }),
  traffic_source_kind: async (kind) => ({ id: kind, name: kind }),
  traffic_source_id: (id) => trafficSourcesService.getById(id),
  flow_id: (id) => flowsService.getById(id),
  rule_group: (id) => flowsService.getRuleGroup(id).then((g) => ({ id: g.id, name: g.name || 'Unnamed group' })),
  landing_page_id: (id) => landingPagesService.getById(id),
  postback_id: (id) => postbacksService.getById(id),
  ad_account_id: (id) => adAccountsService.getById(id),
};
